'use client';

import { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '@/components/common/Sidebar';
import { FileText, Plus, Search, Trash2, Edit, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { listTemplates, saveTemplate, deleteTemplate, TemplateDTO } from '@/services/templates';
import {
  TEMPLATE_TYPE_LABELS,
  TEMPLATE_TYPE_OPTIONS,
  getTemplateTypeLabel,
  normalizeTemplateType,
} from '@/lib/uiLabels';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

export default function TemplatesPage() {
  const { user, logout } = useAuth();
  const [templates, setTemplates] = useState<TemplateDTO[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<Partial<TemplateDTO>>({
    name: '',
    type: 'prescription',
    content: '',
  });

  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await listTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      } else {
        toast.error(response.error || 'Erro ao carregar templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Erro de conexão ao carregar templates');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleOpenCreate = () => {
    setCurrentTemplate({
      name: '',
      type: 'prescription',
      content: '',
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (template: TemplateDTO) => {
    setCurrentTemplate({
      ...template,
      type: normalizeTemplateType(template.type),
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!currentTemplate.name || !currentTemplate.content) {
      toast.error('Preencha o nome e o conteúdo do template');
      return;
    }

    try {
      setIsSaving(true);
      const response = await saveTemplate({
        id: currentTemplate.id,
        name: currentTemplate.name!,
        type: normalizeTemplateType(currentTemplate.type),
        content: currentTemplate.content!,
      });

      if (response.success) {
        toast.success(currentTemplate.id ? 'Template atualizado!' : 'Template criado!');
        setIsModalOpen(false);
        fetchTemplates();
      } else {
        toast.error(response.error || 'Erro ao salvar template');
      }
    } catch (error) {
      toast.error('Erro de conexão ao salvar template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este template?')) return;

    try {
      const response = await deleteTemplate(id);
      if (response.success) {
        toast.success('Template excluído!');
        fetchTemplates();
      } else {
        toast.error(response.error || 'Erro ao excluir template');
      }
    } catch (error) {
      toast.error('Erro de conexão ao excluir template');
    }
  };

  const getTypeBadge = (type: string) => {
    const normalized = normalizeTemplateType(type);
    const label = getTemplateTypeLabel(normalized);
    switch (normalized) {
      case 'prescription': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none">{label}</Badge>;
      case 'certificate': return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 border-none">{label}</Badge>;
      case 'exam_request': return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none">{label}</Badge>;
      default: return <Badge variant="outline">{label}</Badge>;
    }
  };

  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getTemplateTypeLabel(t.type).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Sidebar 
        userRole={user?.role as any || 'DOCTOR'} 
        userName={user?.name || 'App Saúde'} 
        onLogout={logout} 
      />
      
      <main className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          <header className="flex justify-between items-end mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-8 h-8 text-teal-600" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Criador de Templates</h1>
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                Gerencie seus modelos personalizados de receitas, atestados e pedidos de exame.
              </p>
            </div>
            <Button 
              onClick={handleOpenCreate}
              className="bg-teal-600 hover:bg-teal-700 text-white gap-2"
            >
              <Plus className="w-4 h-4" /> Novo Template
            </Button>
          </header>

          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-800 mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Buscar templates por nome ou tipo..." 
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-teal-600 animate-spin mb-4" />
              <p className="text-gray-500">Carregando seus modelos...</p>
            </div>
          ) : filteredTemplates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map(template => (
                <Card key={template.id} className="hover:border-teal-200 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      {getTypeBadge(template.type)}
                      <span className="text-[10px] text-gray-400">
                        {template.lastUsedAt 
                          ? `Usado em ${new Date(template.lastUsedAt).toLocaleDateString('pt-BR')}`
                          : `Criado em ${new Date(template.createdAt).toLocaleDateString('pt-BR')}`}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white mt-2">{template.name}</h3>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="text-xs text-gray-500 line-clamp-3 italic ql-editor !p-0 !min-h-0"
                      dangerouslySetInnerHTML={{ __html: template.content }}
                    />
                  </CardContent>
                  <CardFooter className="flex justify-end gap-2 pt-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(template.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-8 gap-1"
                      onClick={() => handleOpenEdit(template)}
                    >
                      <Edit className="w-3 h-3" /> Editar
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-12 text-center border border-dashed border-gray-200 dark:border-neutral-800">
              <div className="bg-gray-50 dark:bg-neutral-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                {searchTerm ? 'Nenhum template encontrado' : 'Nenhum template cadastrado'}
              </h3>
              <p className="text-gray-500 mb-6 max-w-xs mx-auto">
                {searchTerm 
                  ? 'Tente ajustar sua busca ou limpe o filtro para ver todos os modelos.'
                  : 'Crie seu primeiro modelo de documento para agilizar seus atendimentos.'}
              </p>
              {!searchTerm && (
                <Button 
                  onClick={handleOpenCreate}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" /> Criar Meu Primeiro Template
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{currentTemplate.id ? 'Editar Template' : 'Novo Template'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">Nome</Label>
              <Input 
                id="name" 
                className="col-span-3" 
                value={currentTemplate.name}
                onChange={(e) => setCurrentTemplate({...currentTemplate, name: e.target.value})}
                placeholder="Ex: Receita Simples, Atestado de 3 dias..."
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right">Tipo</Label>
              <div className="col-span-3">
                <Select
                  value={normalizeTemplateType(currentTemplate.type)}
                  onValueChange={(value) =>
                    setCurrentTemplate({
                      ...currentTemplate,
                      type: normalizeTemplateType(value),
                    })
                  }
                  items={TEMPLATE_TYPE_LABELS}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo">
                      {(value) => getTemplateTypeLabel(value as string)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value} label={option.label}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Conteúdo do Modelo</Label>
              <div className="h-[300px] overflow-hidden rounded-md border border-input">
                <ReactQuill 
                  theme="snow" 
                  value={currentTemplate.content} 
                  onChange={(content) => setCurrentTemplate({...currentTemplate, content})}
                  placeholder="Escreva aqui o texto padrão do seu modelo..."
                  className="h-full"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="bg-teal-600 hover:bg-teal-700 text-white min-w-[100px]"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Salvar Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
